import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, CreditCard, Truck, Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ScrollArea } from './ui/scroll-area';
import { notificationsAPI } from '../lib/api';

const typeIcon = (type) => {
    if (type === 'payment') return CreditCard;
    if (type === 'delivery_reminder') return Truck;
    return Info;
};

const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return 'adesso';
    if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
    return `${Math.floor(diff / 86400)} g fa`;
};

export const NotificationBell = ({ variant = 'dark' }) => {
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        try {
            const { data } = await notificationsAPI.list();
            setItems(data.items || []);
            setUnread(data.unread || 0);
        } catch (e) { /* silent */ }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [load]);

    const handleItemClick = async (n) => {
        if (!n.read) {
            try { await notificationsAPI.markRead(n.id); } catch (e) { /* ignore */ }
            setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
            setUnread((u) => Math.max(0, u - 1));
        }
        setOpen(false);
        if (n.url) navigate(n.url);
    };

    const handleMarkAll = async () => {
        try { await notificationsAPI.markAllRead(); } catch (e) { /* ignore */ }
        setItems((prev) => prev.map((x) => ({ ...x, read: true })));
        setUnread(0);
    };

    const iconColor = variant === 'dark' ? 'text-white' : 'text-foreground';

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    className={`relative p-2 rounded hover:bg-black/5 dark:hover:bg-white/10 transition-colors ${iconColor}`}
                    aria-label="Notifiche"
                    data-testid="notification-bell"
                >
                    <Bell size={22} />
                    {unread > 0 && (
                        <span
                            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
                            data-testid="notification-unread-badge"
                        >
                            {unread > 99 ? '99+' : unread}
                        </span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[340px] p-0" data-testid="notification-panel">
                <div className="flex items-center justify-between px-4 py-3 border-b">
                    <span className="font-heading uppercase text-sm tracking-wide">Notifiche</span>
                    {unread > 0 && (
                        <button
                            onClick={handleMarkAll}
                            className="text-xs flex items-center gap-1 text-muted-foreground hover:text-foreground"
                            data-testid="notification-mark-all"
                        >
                            <CheckCheck size={14} /> Segna tutte lette
                        </button>
                    )}
                </div>
                <ScrollArea className="max-h-[380px]">
                    {items.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-10" data-testid="notification-empty">
                            Nessuna notifica
                        </p>
                    ) : (
                        <div className="divide-y">
                            {items.map((n) => {
                                const Icon = typeIcon(n.type);
                                return (
                                    <button
                                        key={n.id}
                                        onClick={() => handleItemClick(n)}
                                        className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-muted/60 transition-colors ${n.read ? '' : 'bg-blue-50/60 dark:bg-blue-950/20'}`}
                                        data-testid={`notification-item-${n.id}`}
                                    >
                                        <div className={`shrink-0 mt-0.5 ${n.read ? 'text-muted-foreground' : 'text-primary'}`}>
                                            <Icon size={18} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className={`text-sm ${n.read ? 'font-normal' : 'font-semibold'} truncate`}>{n.title}</p>
                                            <p className="text-xs text-muted-foreground line-clamp-2">{n.body}</p>
                                            <p className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(n.created_at)}</p>
                                        </div>
                                        {!n.read && <span className="shrink-0 w-2 h-2 rounded-full bg-red-600 mt-1.5" />}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </ScrollArea>
            </PopoverContent>
        </Popover>
    );
};

export default NotificationBell;
